import { useId } from 'react';

import type { LayoutPoint } from '../contracts/models';
import { normalizeLayoutPolygon } from '../geometry/polygons';

export interface SelectedAreaFocusOverlayProps {
  points: readonly LayoutPoint[];
  width: number;
  height: number;
  scale: number;
  className?: string;
}

const clamp = (
  value: number,
  min: number,
  max: number,
): number =>
  max < min ? min : Math.min(Math.max(value, min), max);

/**
 * Ironview 360 reference treatment for isolating a selected AreaTag.
 */
export const SelectedAreaFocusOverlay = ({
  points,
  width,
  height,
  scale,
  className,
}: SelectedAreaFocusOverlayProps) => {
  const reactId = useId();
  if (width <= 0 || height <= 0) return null;

  const polygon = normalizeLayoutPolygon(points);
  if (polygon.length < 3) return null;

  const polygonPoints = polygon
    .map((point) => point.join(','))
    .join(' ');
  const idPrefix = reactId.replace(/:/g, '');
  const controlScale = clamp(
    1 / Math.max(scale, 0.001),
    0.22,
    1.15,
  );
  const rootClassName = [
    'iv-selected-area-focus-overlay',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <g className={rootClassName}>
      <defs>
        <mask id={`${idPrefix}-focus-hole`}>
          <rect
            x={0}
            y={0}
            width={width}
            height={height}
            fill="white"
          />
          <polygon points={polygonPoints} fill="black" />
        </mask>
        <filter
          id={`${idPrefix}-focus-glow`}
          x="-20%"
          y="-20%"
          width="140%"
          height="140%"
        >
          <feDropShadow
            dx="0"
            dy="0"
            stdDeviation={5 * controlScale}
            floodColor="hsl(var(--iv-primary))"
            floodOpacity="0.42"
          />
          <feDropShadow
            dx="0"
            dy={5 * controlScale}
            stdDeviation={5 * controlScale}
            floodColor="hsl(var(--iv-foreground))"
            floodOpacity="0.18"
          />
        </filter>
        <filter
          id={`${idPrefix}-focus-soften`}
          x="-4%"
          y="-4%"
          width="108%"
          height="108%"
        >
          <feGaussianBlur
            stdDeviation={1.8 * controlScale}
          />
        </filter>
      </defs>

      <rect
        x={0}
        y={0}
        width={width}
        height={height}
        fill="hsl(var(--iv-background) / 0.58)"
        mask={`url(#${idPrefix}-focus-hole)`}
        filter={`url(#${idPrefix}-focus-soften)`}
        pointerEvents="none"
      />
      <polygon
        points={polygonPoints}
        fill="hsl(var(--iv-primary) / 0.1)"
        stroke="hsl(var(--iv-primary))"
        strokeWidth={Math.max(1.5, 2.4 * controlScale)}
        strokeLinejoin="round"
        strokeLinecap="round"
        filter={`url(#${idPrefix}-focus-glow)`}
        pointerEvents="none"
      />
    </g>
  );
};
